import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import { useTheme } from "@mui/material/styles";
import { slugify } from "healthex-common/utils";
import * as React from "react";
import styled from "styled-components";
import { ConfirmationContent } from "./HXButton.styles";
import { usePreciseSize } from "./usePreciseSize";
const HXCommonButton = (props) => {
    const { children, ref, loading, onClick, secondary, text, destructiveAction, confirmText, confirmIcon, startIcon, endIcon, ...buttonProps } = props;
    const [autoLoading, setAutoLoading] = React.useState(false);
    const [confirming, setConfirming] = React.useState(false);
    const [fixedSize, setFixedSize] = React.useState({ width: 0, height: 0 });
    const timeoutRef = React.useRef(null);
    const buttonRef = React.useRef(null);
    const [buttonWidth, buttonHeight] = usePreciseSize(buttonRef);
    const theme = useTheme();
    const wrappedOnClick = async (e) => {
        if (onClick) {
            setFixedSize({
                width: buttonWidth,
                height: buttonHeight,
            });
            setAutoLoading(true);
            try {
                const result = await onClick(e);
                if (confirmText && result) {
                    setConfirming(true);
                    if (timeoutRef.current) {
                        window.clearTimeout(timeoutRef.current);
                    }
                    timeoutRef.current = window.setTimeout(() => {
                        setConfirming(false);
                    }, 2000);
                }
            }
            finally {
                setAutoLoading(false);
            }
        }
    };
    const wrappedLoading = loading !== null && loading !== void 0 ? loading : autoLoading;
    const wasLoadingRef = React.useRef(false);
    React.useEffect(() => {
        if (wrappedLoading && !wasLoadingRef.current && buttonWidth > 0 && buttonHeight > 0) {
            setFixedSize({ width: buttonWidth, height: buttonHeight });
        }
        wasLoadingRef.current = wrappedLoading;
    }, [wrappedLoading, buttonWidth, buttonHeight]);
    const fixedSizeProps = fixedSize.width > 0 && fixedSize.height > 0 && (wrappedLoading || confirming)
        ? { style: { ...buttonProps.style, width: fixedSize.width, height: fixedSize.height } }
        : {};
    const iconProps = wrappedLoading || confirming ? undefined : { startIcon, endIcon };
    const bgcolor = destructiveAction ? "error" : props.color;
    let sx = {};
    if (props.removeHorizontalPadding) {
        sx = { paddingLeft: 0, paddingRight: 0 };
    }
    if (props.transparentBackground) {
        sx = { background: "transparent" };
    }
    if (props.transparentBackground) {
        sx = { hover: { backgroundColor: "transparent" } };
    }
    return (_jsx(Button, { ref: buttonRef, onClick: wrappedOnClick, variant: text ? "text" : secondary ? "outlined" : "contained", "data-heap-id": props.analyticsId ? slugify(props.analyticsId) : typeof children === "string" ? slugify(children) : undefined, ...buttonProps, ...fixedSizeProps, ...iconProps, color: bgcolor, sx: sx, children: buttonContent() }));
    function buttonContent() {
        if (wrappedLoading) {
            let color = secondary ? theme.palette.primary.main : "white";
            if (props.loaderColor) {
                color = props.loaderColor;
            }
            if (props.showTextWhileLoading) {
                return (_jsxs(_Fragment, { children: [_jsx(CircularProgress, { className: "loadingButtonCircularProgress", size: 28, sx: {
                                color,
                            } }), _jsx("div", { style: { marginLeft: "10px" }, children: props.children })] }));
            }
            return (_jsx(CircularProgress, { className: "loadingButtonCircularProgress", size: 28, sx: {
                    color,
                } }));
        }
        else if (confirming) {
            return (_jsxs(ConfirmationContent, { children: [confirmIcon && React.createElement(confirmIcon), _jsx("div", { children: confirmText })] }));
        }
        else {
            return props.children;
        }
    }
};
export const HXButton = styled(HXCommonButton) ``;
export const HXMobileButton = styled(HXCommonButton).attrs({
    size: "large",
}) `
    padding: 8px 32px;
    height: 56px;
`;
export const HXIconButton = (props) => {
    const theme = useTheme();
    return (_jsx(IconButton, { ...props, sx: {
            color: theme.palette.primary.main,
        } }));
};
